import axiosInstance from './axios';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { showToast } from '@/components/Toast';

/**
 * 统一的 API 请求模块
 *
 * 基于 axios 实例，提供统一的请求接口
 * 支持自动处理：
 * - 401 未授权（自动跳转登录）
 * - 业务层错误（code !== 200）
 * - 网络错误、超时等
 * - 自动添加 Bearer Token
 * - 登录流程等待
 */

export interface ApiResponse<T = any> {
  code?: number;
  message?: string;
  data?: T;
  [key: string]: any;
}

export interface ApiRequestConfig extends AxiosRequestConfig {
  /** 是否跳过业务层错误自动提示（默认为 false） */
  skipErrorToast?: boolean;
  /** 是否直接返回原始响应（默认为 false，返回 response.data） */
  returnRawResponse?: boolean;
}

/**
 * 统一的 GET 请求
 */
export async function apiGet<T = any>(
  url: string,
  config?: ApiRequestConfig
): Promise<T> {
  return request<T>('GET', url, undefined, config);
}

/**
 * 统一的 POST 请求
 */
export async function apiPost<T = any>(
  url: string,
  data?: any,
  config?: ApiRequestConfig
): Promise<T> {
  return request<T>('POST', url, data, config);
}

/**
 * 统一的 PUT 请求
 */
export async function apiPut<T = any>(
  url: string,
  data?: any,
  config?: ApiRequestConfig
): Promise<T> {
  return request<T>('PUT', url, data, config);
}

/**
 * 统一的 PATCH 请求
 */
export async function apiPatch<T = any>(
  url: string,
  data?: any,
  config?: ApiRequestConfig
): Promise<T> {
  return request<T>('PATCH', url, data, config);
}

/**
 * 统一的 DELETE 请求
 */
export async function apiDelete<T = any>(
  url: string,
  data?: any,
  config?: ApiRequestConfig
): Promise<T> {
  return request<T>('DELETE', url, data, config);
}

/**
 * 核心请求方法
 */
async function request<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: any,
  config?: ApiRequestConfig
): Promise<T> {
  const {
    skipErrorToast = false,
    returnRawResponse = false,
    ...axiosConfig
  } = config || {};
  // eslint-disable-next-line no-useless-catch
  try {
    let response: AxiosResponse;

    // 根据方法类型发起请求
    switch (method) {
      case 'GET':
        response = await axiosInstance.get(url, axiosConfig);
        break;
      case 'POST':
        response = await axiosInstance.post(url, data, axiosConfig);
        break;
      case 'PUT':
        response = await axiosInstance.put(url, data, axiosConfig);
        break;
      case 'PATCH':
        response = await axiosInstance.patch(url, data, axiosConfig);
        break;
      case 'DELETE':
        // DELETE 请求如果有 data，需要通过 config.data 传递
        if (data) {
          response = await axiosInstance.delete(url, {
            ...axiosConfig,
            data,
          });
        } else {
          response = await axiosInstance.delete(url, axiosConfig);
        }
        break;
    }

    // 如果需要返回原始响应
    if (returnRawResponse) {
      return response as T;
    }

    // 获取响应数据
    const responseData = response.data;

    // 业务层错误处理（如果有 code 字段且不为 200）
    if (
      responseData &&
      typeof responseData.code !== 'undefined' &&
      responseData.code !== 200
    ) {
      const errorMessage = responseData.message || 'Request failed';

      // 如果不跳过错误提示，显示 toast
      if (!skipErrorToast) {
        showToast(errorMessage, 'error');
      }

      // 仍然返回数据，让调用方决定如何处理
      return responseData as T;
    }

    // 返回响应数据
    return responseData as T;
  } catch (error) {
    // axios 拦截器已经处理了错误提示，这里直接抛出
    throw error;
  }
}

/**
 * 上传文件的特殊方法（支持进度回调）
 */
export async function apiUpload<T = any>(
  url: string,
  formData: FormData,
  onProgress?: (progress: number) => void,
  config?: ApiRequestConfig
): Promise<T> {
  const {
    skipErrorToast = false,
    returnRawResponse = false,
    ...axiosConfig
  } = config || {};

  // eslint-disable-next-line no-useless-catch
  try {
    const response = await axiosInstance.post(url, formData, {
      ...axiosConfig,
      timeout: 600000, // 10 分钟超时，适用于大文件上传
      headers: {
        'Content-Type': 'multipart/form-data',
        ...axiosConfig.headers,
      },
      onUploadProgress: progressEvent => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          onProgress(progress);
        }
      },
    });

    // 如果需要返回原始响应
    if (returnRawResponse) {
      return response as T;
    }

    // 获取响应数据
    const responseData = response.data;

    // 业务层错误处理
    if (
      responseData &&
      typeof responseData.code !== 'undefined' &&
      responseData.code !== 200
    ) {
      const errorMessage = responseData.message || 'Upload failed';

      if (!skipErrorToast) {
        showToast(errorMessage, 'error');
      }

      return responseData as T;
    }

    return responseData as T;
  } catch (error) {
    throw error;
  }
}

/**
 * 兼容旧代码的方法（逐步迁移后可移除）
 */

/**
 * @deprecated 使用 apiGet/apiPost 等方法替代
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  console.warn(
    'fetchWithAuth is deprecated. Please use apiGet/apiPost instead.'
  );

  const url = typeof input === 'string' ? input : input.toString();
  const method = init?.method?.toUpperCase() || 'GET';
  const body = init?.body;

  try {
    const response = await axiosInstance.request({
      url,
      method,
      data: body,
      headers: init?.headers as any,
    });

    // 模拟 Response 对象
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as any,
    });
  } catch (error: any) {
    if (error.response) {
      return new Response(JSON.stringify(error.response.data), {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
      });
    }
    throw error;
  }
}

/**
 * @deprecated 使用 apiGet/apiPost 等方法替代
 */
export async function fetchJson(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<any> {
  console.warn('fetchJson is deprecated. Please use apiGet/apiPost instead.');

  const url = typeof input === 'string' ? input : input.toString();
  const method = init?.method?.toUpperCase() || 'GET';
  const body = init?.body ? JSON.parse(init.body as string) : undefined;

  if (method === 'GET') {
    return apiGet(url);
  } else if (method === 'POST') {
    return apiPost(url, body);
  } else if (method === 'PUT') {
    return apiPut(url, body);
  } else if (method === 'PATCH') {
    return apiPatch(url, body);
  } else if (method === 'DELETE') {
    return apiDelete(url);
  }

  return apiGet(url);
}
